import React, { cloneElement, useContext } from 'react';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import Button from 'components/common/Button/Button';
import AccountMenu from 'components/MainLayout/BurgersMenu/AccountMenu';
import CompanyMenu from 'components/MainLayout/BurgersMenu/CompanyMenu';
import ContactHfsDialog from 'components/MainLayout/ContactHfsDialog/ContactHfsDialog';
import { InfoWarning, LoginIcon, LogoIcon } from 'components/common/Icons/Icons';
import { ApplicationRoute } from 'routes/routes.enums';
import { RootStoreContext } from 'index';
import MessageForIntermediary from 'components/MainLayout/MessageForIntermediary/MessageForIntermediary';
import { PermissionName } from 'components/permissions/permissions.enum';
import { hasPermission } from 'components/permissions/permissions.utils';
import { ASSIGNING_CONTACT_TEXT } from 'components/accountStatus/accountStatus.constants';
import { AccountStatus } from 'components/users/users.enums';
import Spinner from 'components/common/Spinner/Spinner';
import Tooltip from 'components/common/Tooltip/Tooltip';
import FeaturesRestrictedText from 'components/MainLayout/GlobalModals/FeaturesRestricted/FeaturesRestrictedText';

import styles from './Header.module.scss';
import { HEADER_NAV_ITEMS } from './header.constants';

const Header = () => {
  const { authStore, intermediaryApplicationsStore } = useContext(RootStoreContext);
  const { companyProfile, clearStore, mdaContacts, isAwaitingTeamMemberAssignment } = intermediaryApplicationsStore;
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = authStore.isAuthenticated && authStore.account?.accountStatus === AccountStatus.Active;

  if (isAuthenticated) {
    const intermediaryVariables = companyProfile;
    const userPermissions = authStore.account!.permissions;
    const mdaSignedPermissions = authStore.account?.mdaSignedPermissions;

    const handleLogout = async () => {
      await authStore.logout();
      await clearStore();
      navigate(ApplicationRoute.Login);
    };

    const visibleNavItems = HEADER_NAV_ITEMS.filter((item) =>
      mdaSignedPermissions
        ? hasPermission(mdaSignedPermissions, item.requiredPermissions)
        : hasPermission(userPermissions, item.requiredPermissions),
    );

    const hasConsumerApplicationPermission = mdaSignedPermissions
      ? hasPermission(mdaSignedPermissions, [PermissionName.ConsumerApplication])
      : hasPermission(userPermissions, [PermissionName.ConsumerApplication]);

    return (
      <div className={styles.headerWrapper}>
        <div className={styles.header}>
          <div className={styles.authenticatedHeader}>
            {hasConsumerApplicationPermission ? (
              <Link to={ApplicationRoute.Home}>
                <LogoIcon className={styles.headerImage} />
              </Link>
            ) : (
              <LogoIcon className={styles.headerImage} />
            )}
            <div className={styles.headerButtonGroup}>
              {visibleNavItems.map(({ label, route, icon, isShowIcon }) => (
                <Button
                  key={label}
                  className={styles.headerButton}
                  onClick={() => route && navigate(route)}
                  variant="underline"
                  active={location.pathname === route}
                >
                  {label}
                  {icon && isShowIcon && cloneElement(icon, { className: styles.menuIcon })}
                </Button>
              ))}
            </div>
            <CompanyMenu companyName={intermediaryVariables?.name || ''} />
            <AccountMenu handleLogout={handleLogout} />
          </div>
        </div>
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerTitleContainer}>
            <div className={styles.headerTitleIntermediaryName}>
              <h3 className={styles.title}>{intermediaryVariables?.name} </h3>
              {!companyProfile?.masterDealerAgreementWasSigned && (
                <Tooltip title={<FeaturesRestrictedText mdaContacts={mdaContacts} />} placement="bottom">
                  <span className={styles.warningIconWrap}>
                    <InfoWarning />
                  </span>
                </Tooltip>
              )}
            </div>
            <div className={isAwaitingTeamMemberAssignment ? styles.csmInfoWrapperCentered : styles.csmInfoWrapper}>
              <ContactHfsDialog />
              <div>
                {isAwaitingTeamMemberAssignment ? (
                  <div className={styles.assigningWrapper}>
                    <Spinner size={16} />
                    <p className={styles.text}>{ASSIGNING_CONTACT_TEXT}</p>
                  </div>
                ) : (
                  <>
                    <p className={styles.text}>{intermediaryVariables?.contactManager.name || ''}</p>
                    <p className={styles.text}>{intermediaryVariables?.contactManager.phone || ''}</p>
                    <p className={styles.text}>{intermediaryVariables?.contactManager.email || ''}</p>
                  </>
                )}
              </div>
            </div>
          </div>
          {location.pathname === ApplicationRoute.Home && <MessageForIntermediary />}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.headerWrapper}>
      <div className={styles.header}>
        <Link to={ApplicationRoute.Login} className={styles.logo}>
          <LogoIcon className={styles.headerImage} />
        </Link>
        {location.pathname === ApplicationRoute.Prequalification && (
          <Link to={ApplicationRoute.Login} className={styles.loginContainer}>
            <LoginIcon />
            <span className={styles.loginButton}>Log in</span>
          </Link>
        )}
      </div>
    </div>
  );
};

export default observer(Header);
